import Card, { CardHeader } from '../ui/Card';
import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { documentsApi } from '../../api/documents';
import type { DocumentItem, LinkedCommunicationSummary } from '../../types/index';
import DocumentViewer from './DocumentViewer';
import DocumentEditModal from './DocumentEditModal';
import { STATUS_BADGES } from '../communication/KommunikationSection';
import { useDocumentConfig } from '../../hooks/useConfigVisibility';

const INPUT_CLASS =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

function formatFileSize(bytes: number | null): string {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const DOC_TYPE_LABELS: Record<string, string> = {
  upload: 'Hochgeladen',
  template_generated: 'Aus Vorlage',
  correspondence: 'Korrespondenz',
};

function canPreview(mime: string | null): boolean {
  if (!mime) return false;
  return mime === 'application/pdf' || mime.startsWith('image/');
}

/** Compact metadata line — drop the redundant "Aus Vorlage" prefix when a
 *  template name is present (the template_name alone implies it). */
function metadataLine(doc: DocumentItem): string[] {
  const parts: string[] = [];
  if (doc.document_type === 'template_generated') {
    if (doc.template_name) parts.push(`Vorlage: ${doc.template_name}`);
    else parts.push(DOC_TYPE_LABELS.template_generated);
  } else if (DOC_TYPE_LABELS[doc.document_type]) {
    parts.push(DOC_TYPE_LABELS[doc.document_type]);
  } else {
    parts.push(doc.document_type);
  }
  parts.push(new Date(doc.created_at).toLocaleDateString('de-DE'));
  return parts;
}

const FILE_ICON_CLASS = 'h-5 w-5 text-gray-400 flex-shrink-0';

function FileIcon({ mime }: { mime: string | null }) {
  const isPdf = mime === 'application/pdf';
  const isImage = !!mime && mime.startsWith('image/');
  const isOdf = mime?.startsWith('application/vnd.oasis.opendocument') ?? false;

  if (isImage) {
    return (
      <svg className={FILE_ICON_CLASS} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
      </svg>
    );
  }
  // Generic document outline used for PDF, ODT, and unknown
  return (
    <svg className={FILE_ICON_CLASS} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
      {isPdf && (
        <text x="12" y="20" textAnchor="middle" fontSize="5" fontWeight="bold" fill="currentColor" stroke="none">PDF</text>
      )}
      {isOdf && (
        <text x="12" y="20" textAnchor="middle" fontSize="5" fontWeight="bold" fill="currentColor" stroke="none">ODT</text>
      )}
    </svg>
  );
}

const KIND_LABEL: Record<string, string> = {
  letter: 'Brief',
  email: 'E-Mail',
  call: 'Anruf',
};

function LinkedStatusPill({ linked }: { linked: LinkedCommunicationSummary | null }) {
  if (!linked || !linked.status) return null;
  const badge = STATUS_BADGES[linked.status];
  if (!badge) return null;
  const kindLabel = KIND_LABEL[linked.kind] || linked.kind;
  return (
    <span
      className={`text-xs px-1.5 py-0.5 rounded ${badge.class} whitespace-nowrap`}
      title={`Verknüpft mit ${kindLabel} (${linked.direction === 'incoming' ? 'eingehend' : 'ausgehend'}) — Status: ${badge.label}`}
    >
      {badge.label}
    </span>
  );
}

interface DocumentListProps {
  entityType: string;
  entityId: number;
}

export default function DocumentList({ entityType, entityId }: DocumentListProps) {
  const queryClient = useQueryClient();
  const docConfig = useDocumentConfig(entityType);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editingDoc, setEditingDoc] = useState<DocumentItem | null>(null);
  const [renamingDoc, setRenamingDoc] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState<string>('');
  const [viewingDoc, setViewingDoc] = useState<DocumentItem | null>(null);

  const cacheKey = ['documents', entityType, entityId];

  const { data: documents = [], isLoading } = useQuery({
    queryKey: cacheKey,
    queryFn: () => documentsApi.getDocuments(entityType, entityId),
  });

  const { data: templates = [] } = useQuery({
    queryKey: ['templates'],
    queryFn: () => documentsApi.getTemplates(),
    retry: false,
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => documentsApi.uploadDocument(entityType, entityId, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: cacheKey }),
  });

  const generateMutation = useMutation({
    mutationFn: (templateName: string) => documentsApi.generateDocument(entityType, entityId, templateName),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: cacheKey }),
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, filename }: { id: number; filename: string }) =>
      documentsApi.renameDocument(id, filename),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cacheKey });
      setRenamingDoc(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => documentsApi.deleteDocument(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: cacheKey }),
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadMutation.mutate(file);
      e.target.value = '';
    }
  };

  const startRename = (doc: DocumentItem) => {
    setRenamingDoc(doc.id);
    setRenameValue(doc.filename);
  };

  const saveRename = (docId: number) => {
    const trimmed = renameValue.trim();
    if (!trimmed) {
      setRenamingDoc(null);
      return;
    }
    renameMutation.mutate({ id: docId, filename: trimmed });
  };

  const openDocument = (doc: DocumentItem) => {
    if (canPreview(doc.mime_type)) {
      setViewingDoc(doc);
    } else {
      documentsApi.downloadDocument(doc.id, doc.filename);
    }
  };

  return (
    <Card>
      {/* Section header */}
      <CardHeader className="flex items-center justify-between gap-3">
        <h3 className="text-lg font-medium">
          Dokumente
          <span className="ml-2 text-sm font-normal text-gray-500">({documents.length})</span>
        </h3>
        <div className="flex items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileSelect}
          />
          {docConfig.upload && (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
              className="btn btn-text text-sm"
            >
              {uploadMutation.isPending ? 'Wird hochgeladen...' : '+ Datei hochladen'}
            </button>
          )}
          {docConfig.create && templates.length > 0 && (
            <select
              onChange={(e) => {
                if (e.target.value) {
                  generateMutation.mutate(e.target.value);
                  e.target.value = '';
                }
              }}
              disabled={generateMutation.isPending}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              defaultValue=""
            >
              <option value="" disabled>
                {generateMutation.isPending ? 'Wird erstellt...' : 'Aus Vorlage erstellen...'}
              </option>
              {templates.map((t) => (
                <option key={t.name} value={t.name}>
                  {t.display_name}
                </option>
              ))}
            </select>
          )}
        </div>
      </CardHeader>

      {(uploadMutation.isError || generateMutation.isError || renameMutation.isError) && (
        <div className="px-6 py-2 border-b text-sm text-red-600 space-y-1">
          {uploadMutation.isError && <p>Fehler beim Hochladen.</p>}
          {generateMutation.isError && <p>Fehler beim Erstellen aus Vorlage.</p>}
          {renameMutation.isError && <p>Fehler beim Umbenennen.</p>}
        </div>
      )}

      {/* Documents list */}
      {isLoading ? (
        <div className="px-6 py-6 flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
        </div>
      ) : documents.length === 0 ? (
        <p className="px-6 py-4 text-sm text-gray-500">Noch keine Dokumente vorhanden.</p>
      ) : (
        <ul className="divide-y divide-gray-200">
          {documents.map((doc) => {
            const isRenaming = renamingDoc === doc.id;
            const meta = metadataLine(doc);
            return (
            <li key={doc.id} className="px-6 py-3">
              <div className="flex items-start gap-3">
                <FileIcon mime={doc.mime_type} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {isRenaming ? (
                      <>
                        <input
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveRename(doc.id);
                            if (e.key === 'Escape') setRenamingDoc(null);
                          }}
                          autoFocus
                          className={INPUT_CLASS + ' flex-1'}
                        />
                        <button
                          onClick={() => saveRename(doc.id)}
                          disabled={renameMutation.isPending}
                          className="btn btn-primary text-xs px-3 py-1.5"
                        >
                          Speichern
                        </button>
                        <button
                          onClick={() => setRenamingDoc(null)}
                          className="btn btn-text text-neutral-600 enabled:hover:text-neutral-800 focus-visible:ring-neutral-500 text-xs"
                        >
                          Abbrechen
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => openDocument(doc)}
                          className="btn btn-text hover:underline truncate text-left font-medium text-sm"
                          title={canPreview(doc.mime_type) ? 'In Vorschau öffnen' : 'Herunterladen'}
                        >
                          {doc.filename}
                        </button>
                        <LinkedStatusPill linked={doc.linked_communication ?? null} />
                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {formatFileSize(doc.file_size)}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-1 mt-0.5 flex-wrap text-xs text-gray-500">
                    {meta.map((part, i) => (
                      <span key={i} className={i === 0 ? '' : 'text-gray-400'}>
                        {i > 0 && <span className="mr-1">·</span>}
                        {part}
                      </span>
                    ))}
                    {doc.notes && (
                      <span className="text-gray-500 truncate" title={doc.notes}>
                        <span className="mx-1 text-gray-400">·</span>
                        {doc.notes}
                      </span>
                    )}
                  </div>
                </div>
                {!isRenaming && (
                  <div className="flex gap-3 flex-shrink-0 items-center text-xs">
                    <button
                      onClick={() => documentsApi.downloadDocument(doc.id, doc.filename)}
                      className="btn btn-text text-neutral-600 enabled:hover:text-neutral-800 focus-visible:ring-neutral-500"
                    >
                      Herunterladen
                    </button>
                    <button
                      onClick={() => startRename(doc)}
                      className="btn btn-text text-neutral-600 enabled:hover:text-neutral-800 focus-visible:ring-neutral-500"
                    >
                      Umbenennen
                    </button>
                    <button
                      onClick={() => setEditingDoc(doc)}
                      className="btn btn-text text-neutral-600 enabled:hover:text-neutral-800 focus-visible:ring-neutral-500"
                    >
                      Bearbeiten
                    </button>
                    <button
                      onClick={() => {
                        if (confirm('Dokument wirklich löschen?')) {
                          deleteMutation.mutate(doc.id);
                        }
                      }}
                      className="btn btn-text text-danger-700 enabled:hover:text-danger-900 focus-visible:ring-danger-500"
                    >
                      Löschen
                    </button>
                  </div>
                )}
              </div>
            </li>
            );
          })}
        </ul>
      )}

      {viewingDoc && (
        <DocumentViewer
          document={viewingDoc}
          onClose={() => setViewingDoc(null)}
          onRenamed={(updated) => {
            setViewingDoc(updated);
            queryClient.invalidateQueries({ queryKey: cacheKey });
          }}
        />
      )}

      {editingDoc && (
        <DocumentEditModal document={editingDoc} onClose={() => setEditingDoc(null)} />
      )}
    </Card>
  );
}
